import { Box, Chip } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { TabsContentProps } from "./TabsContent.types";
import {
  Agenda,
  CardTeacher,
  Carousel,
  TabsFilters
} from "../../components";
import {
  CAROUSEL_TYPE,
  LEVEL,
  STATUS_LO
} from "../../consts";
import {
  TypographyDescription,
  TypographyTitle
} from "../../styles/global_styles";
import type { StatusLO } from "../../types";

export const SkillsChip = styled(Chip)(({ theme, variant }) => ({
  backgroundColor: variant === "outlined" ? theme.customColors.backgroundSecondary : theme.customColors.backgroundSecondaryAlternative,
  border: `0.125rem solid ${ variant === "outlined" ? theme.customColors.textTag : theme.customColors.backgroundAccent }`,
  borderRadius: "0.25rem",
  color: theme.customColors.textTag,
  fontSize: "0.75rem",
  fontWeight: 500
}));

export function TabsContentDetail({
  contentAgenda=[],
  contentModules=[],
  currentLO,
  currentTeachers=[],
  currentTutors=[],
  filter,
  setFilter,
  learningObjectTypology
} :
  & Pick<
    TabsContentProps,
    | "contentAgenda"
    | "contentModules"
    | "currentTeachers"
    | "currentTutors"
    | "filter"
    | "setFilter"
    | "learningObjectTypology"
  >
  & Required<
    & Pick<
      TabsContentProps,
      | "currentLO"
    >
    & {
      isDetailsPage: true
    }
  >
) {
  const theme = useTheme();
  const { t } = useTranslation();

  const tabOptions = getTabOptions();
  const isPathOrCourse = learningObjectTypology === LEVEL.COURSE || learningObjectTypology === LEVEL.PATH;  

  return (
    <>
      { currentLO.knowledge && ((currentLO.knowledge.knowledge_areas && currentLO.knowledge.knowledge_areas.length)
      || (currentLO.knowledge.knowledge_domains && currentLO.knowledge.knowledge_domains.length) ) ?
        <>
          <TypographyTitle sx={ { color: theme.customColors.exploreCardText } }>{ t("area_and_domain") }</TypographyTitle>
          <TypographyDescription sx={ { paddingBottom: "40px", display: "flex", flexWrap: "wrap", gap: "1rem" } }>
            { currentLO.knowledge.knowledge_areas?.map((ka) => {
              return <SkillsChip key={ ka.id } label={ ka.name } variant="filled"></SkillsChip>;
            }) }
            { currentLO.knowledge.knowledge_domains?.map(({ name, knowledge_domain_learning_level, id }) => {
              const label =
                `${name.toLocaleUpperCase()}${knowledge_domain_learning_level?"|":""}${(knowledge_domain_learning_level??"").toLocaleUpperCase()}`;
              return <SkillsChip key={ id } label={ label } variant="outlined" ></SkillsChip>;
            }) }
          </TypographyDescription>
        </>: null }
      { currentLO.capabilities && currentLO.capabilities.length ?
        <>
          <TypographyTitle sx={ { color: theme.palette.primary?.main } }>{ t("capabilities") }</TypographyTitle>
          <TypographyDescription sx={ { paddingBottom: "40px", display: "flex", flexWrap: "wrap", gap: "1rem" } }>
            { currentLO.capabilities?.map((c) => {
              return <SkillsChip key={ c.id } label={ c.name.toLocaleUpperCase() } variant="outlined"></SkillsChip>;
            }) }
          </TypographyDescription>
        </>: null }
      <TypographyTitle sx={ { color: theme.customColors.accentVariantA } }>
        { t("what_to_expect") }
      </TypographyTitle>
      <TypographyDescription
        sx={ { paddingBottom: "40px" } }
      >
        { currentLO.longDescription }
      </TypographyDescription>
      {
        contentAgenda.length > 0 ? (
          <Agenda
            title={ t("agenda") }
            steps={ contentAgenda }
          />
        ) : null
      }
      {
        !isPathOrCourse && currentTeachers && currentTeachers?.length > 0 ? (
          <>
            <TypographyTitle>
              { t("teachers") }
            </TypographyTitle>
            <Box sx={ { marginBottom: theme.spacing(4) } }>
              <Carousel
                label={ "teachers-carousel" }
                carouselType={ CAROUSEL_TYPE.DEFAULT }
                hasPagination={ false }
              >
                {
                  currentTeachers?.map((item, id) => (
                    <CardTeacher
                      key={ id.toString() }
                      id={ item.id }
                      image={ item.image }
                      overTitle={ item.operationalField || "" }
                      title={ item.completeName || item?.toString() }
                    />
                  ))
                }
              </Carousel>
            </Box>
          </>
        ) : null
      }
      {
        currentTutors && currentTutors?.length > 0 ? (
          <>
            <TypographyTitle>
              { t("tutors") }
            </TypographyTitle>
            <Box sx={ { marginBottom: theme.spacing(4) } }>
              <Carousel
                label={ "teachers-carousel" }
                carouselType={ CAROUSEL_TYPE.DEFAULT }
                hasPagination={ false }
              >
                {
                  currentTutors?.map((item, id) => (
                    <CardTeacher
                      key={ id.toString() }
                      id={ item.id }
                      image={ item.image }
                      overTitle={ item.operationalField || "" }
                      title={ item.completeName || item?.toString() }
                    />
                  ))
                }
              </Carousel>
            </Box>
          </>
        ) : null
      }
      {
        (currentLO?.learningObjectTypology === LEVEL.PATH) ? (
          <TabsFilters<StatusLO>
            contents={ contentModules }
            filter={ filter }
            options={ tabOptions }
            setFilter={ setFilter }
            title={ t("modules") }
          />
        ) : null
      }
    </>
  );

  function getTabOptions() {
    return [
      {
        children: t("completed"),
        value: STATUS_LO.C
      },
      {
        children: t("in_progress"),
        value: STATUS_LO.P
      },
      {
        children: t("to_start"),
        value: STATUS_LO.E
      }
    ];
  }
}
